<template>
  <div
    class="admin-layout min-vh-100 navbar-app"
    :data-admin-theme="adminThemeKey"
    :style="adminThemeStyle"
  >
    <AppLoginWelcomeOverlay />

    <!-- NAVBAR -->
    <nav class="navbar navbar-expand-xl navbar-dark navbar-fixed admin-navbar-themed" data-tour="admin-navbar">
      <div class="container-fluid">
        <!-- Brand -->
        <router-link class="navbar-brand fw-bold" :to="{ name: 'Sites' }">
          <img src="../assets/logo.png" style="width: 30px;" />
        </router-link>

        <router-link
          v-if="showSitesShortcut"
          class="btn btn-outline-light btn-sm navbar-icon-button navbar-sites-shortcut ms-2"
          :to="{ name: 'Sites' }"
          title="Mes sites"
          aria-label="Accéder à mes sites"
        >
          <i class="fa fa-store"></i>
        </router-link>

        <!-- Burger -->
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#adminNavbar"
          aria-controls="adminNavbar" aria-expanded="false" aria-label="Toggle navigation">
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Links -->
        <div class="collapse navbar-collapse" id="adminNavbar">
          <div class="admin-navbar-scroll">
            <ul
              class="navbar-nav mb-2 mb-xl-0"
              data-tour="admin-navigation"
              @pointerover="handleNavigationPointerOver"
              @pointerout="handleNavigationPointerOut"
              @focusin="handleNavigationFocusIn"
              @focusout="handleNavigationFocusOut"
            >
              <!-- TABLEAU DE BORD -->
              <li v-if="can('dashboard.view')" class="nav-item">
                <router-link class="nav-link" active-class="active" :to="{ name: 'Dashboard' }"
                  :class="{ active: isRouteIn(['Dashboard']) }">
                  <i class="fa-solid fa-table"></i> Tableau de bord
                </router-link>
              </li>

            <!-- ACHATS -->
            <li v-if="canAny(['purchases.view', 'supplier_returns.view', 'suppliers.view'])" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="dropdownAchats" role="button" data-bs-toggle="dropdown"
                aria-expanded="false" :class="{ active: isRouteIn(['Purchases', 'PurchaseShow', 'SupplierReturns', 'SupplierReturnCreate', 'SupplierReturnEdit', 'SupplierReturnShow', 'Suppliers', 'SupplierShow']) }">
                <i class="fa-solid fa-bag-shopping"></i> Achats
              </a>
              <ul class="dropdown-menu" aria-labelledby="dropdownAchats">
                <li v-if="can('purchases.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Purchases' }"
                    :class="{ active: isRouteIn(['Purchases', 'PurchaseShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Commandes d'achats
                  </router-link>
                </li>
                <li v-if="can('supplier_returns.view')">
                  <router-link class="dropdown-item" :to="{ name: 'SupplierReturns' }"
                    :class="{ active: isRouteIn(['SupplierReturns', 'SupplierReturnCreate', 'SupplierReturnEdit', 'SupplierReturnShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Retours fournisseur
                  </router-link>
                </li>
                <li v-if="can('suppliers.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Suppliers' }"
                    :class="{ active: isRouteIn(['Suppliers', 'SupplierShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Fournisseurs
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- STOCK -->
            <li v-if="canAny(['products.view', 'promotions.view', 'inventories.view', 'stock_movements.view', 'stock_transfers.view'])" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="dropdownStock" role="button" data-bs-toggle="dropdown"
                aria-expanded="false" :class="{
                  active: isRouteIn([
                    'ProductDatabase',
                    'ProductPromotions',
                    'Products',
                    'ProductShow',
                    'StockMovements',
                    'StockCalendar',
                    'StockTransfers',
                    'StockTransferCreate',
                    'StockTransferEdit',
                    'StockTransferShow',
                    'Inventories',
                    'InventoryShow',
                    'InventoryCount',
                    'Rays',
                    'RayShow',
                    'RayProductAssignment',
                    'Categories',
                    'CategoryShow',
                    'CategoryProductAssignment',
                  ]),
                }">
                <i class="fa-solid fa-database"></i> Stock
              </a>
              <ul class="dropdown-menu" aria-labelledby="dropdownStock">
                <li v-if="(isGerant || isSupport) && can('products.view')">
                  <router-link class="dropdown-item" :to="{ name: 'ProductDatabase' }"
                    :class="{ active: isRouteIn(['ProductDatabase']) }">
                    <i class="fa-solid fa-caret-right"></i> Base de donnée
                  </router-link>
                </li>
                <li v-if="can('products.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Products' }"
                    :class="{ active: isRouteIn(['Products', 'ProductShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Produits
                  </router-link>
                </li>
                <li v-if="can('promotions.view')">
                  <router-link class="dropdown-item" :to="{ name: 'ProductPromotions' }"
                    :class="{ active: isRouteIn(['ProductPromotions']) }">
                    <i class="fa-solid fa-caret-right"></i> Promotions
                  </router-link>
                </li>
                <li v-if="can('inventories.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Inventories' }"
                    :class="{ active: isRouteIn(['Inventories', 'InventoryShow', 'InventoryCount']) }">
                    <i class="fa-solid fa-caret-right"></i> Inventaires
                  </router-link>
                </li>
                <li v-if="can('stock_movements.view')">
                  <router-link class="dropdown-item" :to="{ name: 'StockCalendar' }"
                    :class="{ active: isRouteIn(['StockCalendar']) }">
                    <i class="fa-solid fa-caret-right"></i> Calendrier
                  </router-link>
                </li>
                <li v-if="can('wallet.view')">
                  <router-link class="dropdown-item" :to="{ name: 'ElectronicWallets' }"
                    :class="{ active: isRouteIn(['ElectronicWallets']) }">
                    <i class="fa-solid fa-caret-right"></i> Portefeuilles
                  </router-link>
                </li>
                <li v-if="can('stock_movements.view')">
                  <router-link class="dropdown-item" :to="{ name: 'StockMovements' }"
                    :class="{ active: isRouteIn(['StockMovements']) }">
                    <i class="fa-solid fa-caret-right"></i> Mouvements
                  </router-link>
                </li>
                <li v-if="can('stock_transfers.view')">
                  <router-link class="dropdown-item" :to="{ name: 'StockTransfers' }"
                    :class="{ active: isRouteIn(['StockTransfers', 'StockTransferCreate', 'StockTransferEdit', 'StockTransferShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Transferts
                  </router-link>
                </li>
                <li v-if="can('products.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Rays' }" :class="{ active: isRouteIn(['Rays', 'RayShow', 'RayProductAssignment']) }">
                    <i class="fa-solid fa-caret-right"></i> Rayons
                  </router-link>
                </li>
                <li v-if="can('products.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Categories' }"
                    :class="{ active: isRouteIn(['Categories', 'CategoryShow', 'CategoryProductAssignment']) }">
                    <i class="fa-solid fa-caret-right"></i> Catégories
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- CLIENTS -->
            <li v-if="can('clients.view')" class="nav-item">
              <router-link class="nav-link" active-class="active" :to="{ name: 'Clients' }"
                :class="{ active: isRouteIn(['Clients', 'ClientShow']) }">
                <i class="fa-solid fa-user-tag"></i> Clients
              </router-link>
            </li>

            <!-- VENTES -->
            <li v-if="canAny(['sales.view', 'cash_registers.view', 'cash_counts.view', 'deliveries.view'])" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle text-uppercase" href="#" id="dropdownVentes" role="button"
                data-bs-toggle="dropdown" aria-expanded="false"
                :class="{ active: isRouteIn(['Sales', 'SalesQuotes', 'SalesDaySummary', 'SaleShow', 'CashRegisters', 'CashRegisterShow', 'CashCounts', 'CashCountShow', 'DeliveriesView', 'DeliveryShow']) }">
                <i class="fa-solid fa-money-bills"></i> Ventes
              </a>
              <ul class="dropdown-menu" aria-labelledby="dropdownVentes">
                <li v-if="can('sales.view')">
                  <router-link class="dropdown-item" :to="{ name: 'Sales' }"
                    :class="{ active: isRouteIn(['Sales', 'SaleShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Historique ventes
                  </router-link>
                </li>
                <li v-if="can('sales.view')">
                  <router-link class="dropdown-item" :to="{ name: 'SalesQuotes' }"
                    :class="{ active: isRouteIn(['SalesQuotes']) }">
                    <i class="fa-solid fa-caret-right"></i> Devis
                  </router-link>
                </li>
                <li v-if="can('cash_counts.view')">
                  <router-link class="dropdown-item" :to="{ name: 'SalesDaySummary' }"
                    :class="{ active: isRouteIn(['SalesDaySummary']) }">
                    <i class="fa-solid fa-caret-right"></i> Journées
                  </router-link>
                </li>
                <li v-if="can('cash_registers.view')">
                  <router-link class="dropdown-item" :to="{ name: 'CashRegisters' }"
                    :class="{ active: isRouteIn(['CashRegisters', 'CashRegisterShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Caisse
                  </router-link>
                </li>
                <li v-if="can('cash_counts.view')">
                  <router-link class="dropdown-item" :to="{ name: 'CashCounts' }"
                    :class="{ active: isRouteIn(['CashCounts', 'CashCountShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Billetage
                  </router-link>
                </li>
                <li v-if="ecommerceEnabled && can('deliveries.view')">
                  <router-link class="dropdown-item" :to="{ name: 'DeliveriesView' }"
                    :class="{ active: isRouteIn(['DeliveriesView', 'DeliveryShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Livraisons
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- DÉPENSES -->
            <li v-if="canAny(['expenses.view', 'expenses.create', 'expenses.manage_recurring'])" class="nav-item">
              <router-link class="nav-link" :to="{ name: 'Expenses' }" :class="{ active: isRouteIn(['Expenses', 'ExpenseShow', 'ExpenseEdit']) }">
                <i class="fa-solid fa-receipt"></i> Dépenses
              </router-link>
            </li>

            <!-- RAPPORTS -->
            <li v-if="can('reports.view')" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="dropdownReports" role="button" data-bs-toggle="dropdown"
                aria-expanded="false" :class="{
                  active: [
                    'SalesReport',
                    'RevenueMarginsReport',
                    'PurchasesReport',
                    'InventoriesReport',
                    'ProductsReport',
                    'SupplierReturnsReport',
                    'SupplierPaymentsReport',
                    'StockMovementsReport',
                    'StockTransfersReport',
                    'CashRegistersReport',
                    'DeliveriesReport',
                    'AccountingJournalReport',
                    'AccountingLedgerReport',
                    'AccountingBalanceReport',
                    'AccountingExceptionsReport',
                    'ExpensesReport',
                  ].includes(String($route.name)),
                }">
                <i class="fa-solid fa-chart-simple"></i> Rapports
              </a>
              <ul class="dropdown-menu" aria-labelledby="dropdownReports">
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'SalesReport' }"
                    :to="{ name: 'SalesReport' }">
                      <i class="fa-solid fa-caret-right"></i> Rapports des ventes
                  </router-link>
                </li>
                <li v-if="can('wallet.report')">
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'WalletsReport' }"
                    :to="{ name: 'WalletsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Portefeuilles électroniques
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'RevenueMarginsReport' }"
                    :to="{ name: 'RevenueMarginsReport' }">
                      <i class="fa-solid fa-caret-right"></i> Chiffre d’affaires & marges
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'PurchasesReport' }"
                    :to="{ name: 'PurchasesReport' }">
                    <i class="fa-solid fa-caret-right"></i> Rapports des achats
                  </router-link>
                </li>
                <li v-if="can('expenses.view')">
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'ExpensesReport' }"
                    :to="{ name: 'ExpensesReport' }">
                    <i class="fa-solid fa-caret-right"></i> Rapport des dépenses
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'InventoriesReport' }"
                    :to="{ name: 'InventoriesReport' }">
                    <i class="fa-solid fa-caret-right"></i> Rapports des inventaires
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'ProductsReport' }"
                    :to="{ name: 'ProductsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Rapports des produits
                  </router-link>
                </li>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'StockMovementsReport' }"
                    :to="{ name: 'StockMovementsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Mouvements de stock
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'StockTransfersReport' }"
                    :to="{ name: 'StockTransfersReport' }">
                    <i class="fa-solid fa-caret-right"></i> Transferts de stock
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'SupplierReturnsReport' }"
                    :to="{ name: 'SupplierReturnsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Retours fournisseur
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'SupplierPaymentsReport' }"
                    :to="{ name: 'SupplierPaymentsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Paiements fournisseur
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'CashRegistersReport' }"
                    :to="{ name: 'CashRegistersReport' }">
                    <i class="fa-solid fa-caret-right"></i> Sessions de caisse
                  </router-link>
                </li>
                <li v-if="ecommerceEnabled">
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'DeliveriesReport' }"
                    :to="{ name: 'DeliveriesReport' }">
                    <i class="fa-solid fa-caret-right"></i> Livraisons
                  </router-link>
                </li>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'AccountingJournalReport' }"
                    :to="{ name: 'AccountingJournalReport' }">
                    <i class="fa-solid fa-caret-right"></i> Journal comptable
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'AccountingLedgerReport' }"
                    :to="{ name: 'AccountingLedgerReport' }">
                    <i class="fa-solid fa-caret-right"></i> Grand livre
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'AccountingBalanceReport' }"
                    :to="{ name: 'AccountingBalanceReport' }">
                    <i class="fa-solid fa-caret-right"></i> Balance comptable
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :class="{ active: $route.name === 'AccountingExceptionsReport' }"
                    :to="{ name: 'AccountingExceptionsReport' }">
                    <i class="fa-solid fa-caret-right"></i> Anomalies comptables
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- UTILISATEURS -->
            <li v-if="can('users.view')" class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" href="#" id="dropdownUsers" role="button" data-bs-toggle="dropdown"
                aria-expanded="false" :class="{ active: isRouteIn(['Users', 'UserShow', 'ActivityLogs']) }">
                <i class="fa-solid fa-users"></i> Utilisateurs
              </a>
              <ul class="dropdown-menu" aria-labelledby="dropdownUsers">
                <li>
                  <router-link class="dropdown-item" :to="{ name: 'Users' }"
                    :class="{ active: isRouteIn(['Users', 'UserShow']) }">
                    <i class="fa-solid fa-caret-right"></i> Comptes utilisateurs
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" :to="{ name: 'ActivityLogs' }"
                    :class="{ active: isRouteIn(['ActivityLogs']) }">
                    <i class="fa-solid fa-caret-right"></i> Activités récentes
                  </router-link>
                </li>
              </ul>
            </li>

              <!-- PARAMÈTRES -->
              <li v-if="canAny(['settings.view', 'settings.update', 'expenses.manage_types'])" class="nav-item">
                <router-link
                  class="nav-link"
                  :to="{ name: settingsLandingRoute }"
                  :class="{ active: isRouteIn(['SettingsGeneral', 'SettingsPrinting', 'SettingsInventory', 'SettingsAccounting', 'ShopInfo', 'DeliverySettings', 'MovementSettings', 'ExpenseTypes', 'SiteShow']) }"
                >
                  <i class="fa fa-cog me-2"></i> Paramètres
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Menu utilisateur -->
          <div class="d-flex align-items-center gap-2 admin-navbar-actions">
            <SupportTicketButton :module-options="supportTicketModuleOptions" />
            <UserMenu :show-settings="false" />
          </div>
        </div>
      </div>
    </nav>

    <!-- CONTENU -->
    <main class="container py-4" data-tour="page-content">
      <div v-if="canUseAdminLayout">
        <AppBreadcrumbs />
        <PageTutorialLauncher />
        <slot name="content"></slot>
      </div>

      <div v-else class="alert alert-warning mt-4">
        <h5 class="alert-heading mb-2">Accès restreint</h5>
        <p class="mb-0">
          Vous n’avez pas les droits nécessaires pour consulter cette section.
          Veuillez contacter votre administrateur si vous pensez qu’il s’agit d’une
          erreur.
        </p>
      </div>
    </main>
    <AppFooter>
      <template #center>
        <CurrentDateTime class="admin-footer-date-time" />
      </template>
    </AppFooter>
  </div>
</template>

<script>
import { Dropdown as BootstrapDropdown } from 'bootstrap';
import AppLoginWelcomeOverlay from '@/components/AppLoginWelcomeOverlay.vue';
import AppFooter from '@/components/AppFooter.vue';
import UserMenu from '@/components/UserMenu.vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import CurrentDateTime from '@/components/CurrentDateTime.vue';
import PageTutorialLauncher from '@/components/PageTutorialLauncher.vue';
import SupportTicketButton from '@/components/SupportTicketButton.vue';
import {
  adminThemeStyle as buildAdminThemeStyle,
  getAdminTheme,
} from '@/support/adminThemes';
import { canBypassPermissions, hasAllPermissions, hasPermission, isDemoUser, isSupportUser, routePermissions } from '@/support/permissions';
import { isMultiSiteAdmin } from '@/support/multiSiteAdmin';
import {
  ADMIN_SUPPORT_TICKET_MODULES,
  ALWAYS_AVAILABLE_SUPPORT_TICKET_MODULES,
} from '@/support/supportTicketModules';

export default {
  name: 'AdminLayout',
  components: {
    AppLoginWelcomeOverlay,
    AppFooter,
    UserMenu,
    AppBreadcrumbs,
    CurrentDateTime,
    PageTutorialLauncher,
    SupportTicketButton,
  },
  data() {
    return { dropdownCloseTimers: new Map() };
  },
  computed: {
    ecommerceEnabled() {
      return this.$store.getters['runtime/moduleEnabled']('ecommerce');
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    isGerant() {
      const user = this.currentUser;
      return user && user.role === 'gerant';
    },
    isSupport() {
      return isSupportUser(this.currentUser);
    },
    settingsLandingRoute() {
      return this.canAny(['settings.view', 'settings.update']) ? 'SettingsGeneral' : 'ExpenseTypes';
    },
    canUseAdminLayout() {
      const permissions = routePermissions(this.$route);

      return !!this.currentUser && hasAllPermissions(this.currentUser, permissions)
        && permissions.every((permission) => {
          const module = permission.split('.', 1)[0];
          return !module || this.$store.getters['runtime/moduleEnabled'](module);
        });
    },
    shopName() {
      return this.$store.getters['shop/shopName'];
    },
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    showSitesShortcut() {
      const isGlobalUser = this.currentUser?.role === 'utilisateur' && !this.currentUser?.shop_id;

      return isMultiSiteAdmin(this.currentUser)
        || (this.isSupport || isGlobalUser)
          && this.currentShopId
          && !this.currentUser?.shop_id
          && this.can('shops.view');
    },
    userSettings() {
      return this.$store.getters['userSettings/settings'] || {};
    },
    adminTheme() {
      return getAdminTheme(this.userSettings?.appearance?.admin_theme);
    },
    adminThemeKey() {
      return this.adminTheme.key;
    },
    adminThemeStyle() {
      return buildAdminThemeStyle(this.adminThemeKey);
    },
    supportTicketModuleOptions() {
      const user = this.currentUser;
      const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
      const hasImplicitAccess = canBypassPermissions(user) || (isDemoUser(user) && permissions.length === 0);
      const availableModules = ADMIN_SUPPORT_TICKET_MODULES.filter(({ permissionModule }) => {
        const isEnabled = this.$store.getters['runtime/moduleEnabled'](permissionModule);
        const isPermitted = hasImplicitAccess
          || permissions.some((permission) => String(permission).startsWith(`${permissionModule}.`));

        return isEnabled && isPermitted;
      });

      return [...availableModules, ...ALWAYS_AVAILABLE_SUPPORT_TICKET_MODULES];
    },
  },
  methods: {
    navigationDropdownFromEvent(event) {
      return event.target?.closest?.('.nav-item.dropdown') || null;
    },
    navigationDropdownToggle(dropdown) {
      return dropdown?.querySelector?.(':scope > [data-bs-toggle="dropdown"]') || null;
    },
    supportsHoverNavigation() {
      return typeof window !== 'undefined'
        && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    },
    openNavigationDropdown(dropdown) {
      const toggle = this.navigationDropdownToggle(dropdown);
      if (!toggle) return;
      const closeTimer = this.dropdownCloseTimers.get(dropdown);
      if (closeTimer) window.clearTimeout(closeTimer);
      this.dropdownCloseTimers.delete(dropdown);
      BootstrapDropdown.getOrCreateInstance(toggle).show();
    },
    scheduleNavigationDropdownClose(dropdown) {
      const toggle = this.navigationDropdownToggle(dropdown);
      if (!toggle) return;
      const previousTimer = this.dropdownCloseTimers.get(dropdown);
      if (previousTimer) window.clearTimeout(previousTimer);
      const closeTimer = window.setTimeout(() => {
        BootstrapDropdown.getOrCreateInstance(toggle).hide();
        this.dropdownCloseTimers.delete(dropdown);
      }, 150);
      this.dropdownCloseTimers.set(dropdown, closeTimer);
    },
    handleNavigationPointerOver(event) {
      if (!this.supportsHoverNavigation()) return;
      const dropdown = this.navigationDropdownFromEvent(event);
      if (dropdown && !dropdown.contains(event.relatedTarget)) this.openNavigationDropdown(dropdown);
    },
    handleNavigationPointerOut(event) {
      if (!this.supportsHoverNavigation()) return;
      const dropdown = this.navigationDropdownFromEvent(event);
      if (dropdown && !dropdown.contains(event.relatedTarget)) this.scheduleNavigationDropdownClose(dropdown);
    },
    handleNavigationFocusIn(event) {
      if (!this.supportsHoverNavigation()) return;
      const dropdown = this.navigationDropdownFromEvent(event);
      if (dropdown) this.openNavigationDropdown(dropdown);
    },
    handleNavigationFocusOut(event) {
      if (!this.supportsHoverNavigation()) return;
      const dropdown = this.navigationDropdownFromEvent(event);
      if (dropdown && !dropdown.contains(event.relatedTarget)) this.scheduleNavigationDropdownClose(dropdown);
    },
    can(permission) {
      const module = String(permission || '').split('.', 1)[0];
      return hasPermission(this.currentUser, permission)
        && (!module || this.$store.getters['runtime/moduleEnabled'](module));
    },
    canAny(permissions) {
      return permissions.some((permission) => this.can(permission));
    },
    isRouteIn(names) {
      return names.includes(String(this.$route.name));
    },
  },
  beforeUnmount() {
    this.dropdownCloseTimers.forEach((timer) => window.clearTimeout(timer));
    this.dropdownCloseTimers.clear();
  },
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--suite241-page-bg, var(--bs-body-bg));
}

.admin-layout main {
  flex: 1 1 auto;
}

.admin-layout :deep(.btn-primary) {
  --bs-btn-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-hover-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-disabled-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-disabled-border-color: var(--suite241-admin-primary, #0d6efd);
}

.admin-layout :deep(.btn-outline-primary) {
  --bs-btn-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-bg: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-hover-border-color: var(--suite241-admin-primary, #0d6efd);
  --bs-btn-active-bg: var(--suite241-admin-primary-hover, #0b5ed7);
  --bs-btn-active-border-color: var(--suite241-admin-primary-hover, #0b5ed7);
}

.admin-layout :deep(.card),
.admin-layout :deep(.settings-content-card) {
  background-color: var(--suite241-card-bg, #ffffff);
  border-color: var(--suite241-card-border, #e5e7eb);
}

/* NAVBAR FIXE STYLE "LOGICIEL" */
.navbar-fixed {
  background: var(--suite241-admin-nav-bg, #0d6efd);
  position: sticky;
  top: 0;
  z-index: 1030;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}

/* Espace en haut pour ne pas que le main passe sous la navbar */
.admin-layout main {
  padding-top: 4.5rem;
}

/* Conteneur app pour les styles de navbar */
.navbar-app .navbar {
  padding-top: 0.35rem;
  padding-bottom: 0.35rem;
}

/* Brand (nom de l'app) */
.navbar-app .navbar-brand {
  text-transform: uppercase;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
}

/* Liens du menu (onglets) */
.admin-navbar-scroll {
  flex: 1 1 auto;
  min-width: 0;
}

.navbar-app .nav-link {
  align-items: center;
  text-transform: uppercase;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 600;
  gap: 0.42rem;
  padding: 0.55rem 0.95rem;
  margin-right: 0.25rem;
  border-radius: 0.6rem 0.6rem 0 0;
  color: var(--suite241-admin-nav-text, rgba(255, 255, 255, 0.68));
  position: relative;
  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.1s ease;
  white-space: nowrap;
}

/* Hover */
.navbar-app .nav-link:hover {
  color: #ffffff;
  background-color: var(--suite241-admin-nav-hover-bg, rgba(255, 255, 255, 0.08));
}

.admin-layout :deep(.dropdown-toggle::after) {
  transform: rotate(-90deg);
  transform-origin: center;
  transition: transform 0.2s ease;
}

.admin-layout :deep(.dropdown-toggle:hover::after),
.admin-layout :deep(.dropdown-toggle:focus-visible::after),
.admin-layout :deep(.dropdown-toggle[aria-expanded='true']::after),
.admin-layout :deep(.dropdown.show > .dropdown-toggle::after) {
  transform: rotate(0deg);
}

/* Onglet actif — style logiciel bien marqué */
.navbar-app .nav-link.active {
  color: #ffffff !important;
  background-color: var(--suite241-admin-nav-active-bg, rgba(0, 0, 0, 0.15));
  box-shadow: inset 0 -2px 6px rgba(0, 0, 0, 0.35);
  transform: translateY(1px);
}

/* Trait lumineux en haut de l'onglet actif */
.navbar-app .nav-link.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 12%;
  width: 76%;
  height: 3px;
  background-color: var(--suite241-admin-nav-accent, #ffffff);
  border-radius: 999px;
  opacity: 0.95;
}

/* Dropdown : on garde lisible, pas en full uppercase */
.navbar-app .dropdown-menu .dropdown-item {
  align-items: center;
  border-radius: 0.55rem;
  display: flex;
  gap: 0.45rem;
  font-size: 0.8rem;
  margin: 0.12rem 0.35rem;
  padding: 0.55rem 0.7rem;
  position: relative;
  text-transform: none;
  transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
  width: calc(100% - 0.7rem);
  white-space: nowrap;
}

.navbar-app .dropdown-menu {
  backdrop-filter: blur(12px);
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.75rem;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.2), 0 4px 12px rgba(15, 23, 42, 0.08);
  margin-top: 0.35rem;
  padding: 0.45rem 0;
  z-index: 1045;
}

.navbar-app .dropdown-menu::before {
  background: var(--suite241-admin-nav-accent, var(--suite241-admin-primary, #0d6efd));
  border-radius: 999px;
  content: '';
  height: 3px;
  left: 0.75rem;
  position: absolute;
  right: 0.75rem;
  top: 0;
}

.navbar-app .dropdown-menu.show {
  animation: admin-dropdown-reveal 0.18s ease-out both;
}

.navbar-app .dropdown-menu .dropdown-item:hover,
.navbar-app .dropdown-menu .dropdown-item:focus {
  background: rgba(13, 110, 253, 0.09);
  background: color-mix(in srgb, var(--suite241-admin-primary, #0d6efd) 10%, white);
  color: var(--suite241-admin-primary, #0d6efd);
  transform: translateX(2px);
}

.navbar-app .dropdown-menu .dropdown-item:hover i,
.navbar-app .dropdown-menu .dropdown-item:focus i {
  color: var(--suite241-admin-primary, #0d6efd);
}

.navbar-app .dropdown-menu .dropdown-item.active {
  background: rgba(13, 110, 253, 0.14);
  background: color-mix(in srgb, var(--suite241-admin-primary, #0d6efd) 16%, white);
  color: var(--suite241-admin-primary, #0d6efd);
  font-weight: 700;
}

.navbar-app .dropdown-menu .dropdown-item.active::before {
  background: var(--suite241-admin-primary, #0d6efd);
  border-radius: 999px;
  bottom: 0.42rem;
  content: '';
  left: 0.18rem;
  position: absolute;
  top: 0.42rem;
  width: 3px;
}

@keyframes admin-dropdown-reveal {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .admin-layout :deep(.dropdown-toggle::after) { transition: none; }
  .navbar-app .dropdown-menu.show { animation: none; }
  .navbar-app .dropdown-menu .dropdown-item { transition: none; }
  .navbar-app .dropdown-menu .dropdown-item:hover,
  .navbar-app .dropdown-menu .dropdown-item:focus { transform: none; }
}

/* Texte utilisateur à droite */
.navbar-app .text-white.small {
  font-size: 0.78rem;
}

/* Bouton déconnexion un peu plus doux */
.navbar-app .btn-outline-light.btn-sm {
  border-radius: 999px;
  padding-inline: 0.9rem;
  font-size: 0.78rem;
}

.navbar-app .navbar-icon-button {
  align-items: center;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  padding-inline: 0;
  width: 32px;
}

.navbar-app .navbar-sites-shortcut {
  border: 0;
  color: #ffffff;
  margin-right: 0.65rem;
  transition: background-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.navbar-app .navbar-sites-shortcut i {
  font-size: 1.12rem;
  transition: transform 0.18s ease;
}

.navbar-app .navbar-sites-shortcut:hover,
.navbar-app .navbar-sites-shortcut:focus-visible {
  background-color: rgba(255, 255, 255, 0.14);
  box-shadow: 0 0 0 0.16rem rgba(255, 255, 255, 0.24);
  color: #ffffff;
  transform: translateY(-2px);
}

.navbar-app .navbar-sites-shortcut:hover i,
.navbar-app .navbar-sites-shortcut:focus-visible i {
  animation: navbar-action-pop 0.34s ease-out;
}

@keyframes navbar-action-pop {
  0% { transform: scale(1) rotate(0); }
  55% { transform: scale(1.18) rotate(-8deg); }
  100% { transform: scale(1) rotate(0); }
}

@media (prefers-reduced-motion: reduce) {
  .navbar-app .navbar-sites-shortcut,
  .navbar-app .navbar-sites-shortcut i {
    transition: none;
  }

  .navbar-app .navbar-sites-shortcut:hover i,
  .navbar-app .navbar-sites-shortcut:focus-visible i {
    animation: none;
  }
}

.admin-navbar-actions {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
}

.admin-layout .admin-footer-date-time {
  border-color: var(--suite241-admin-footer-border, rgba(15, 80, 140, 0.22));
  color: var(--suite241-admin-footer-text, #0b508c);
}

.admin-layout .admin-footer-date-time :deep(i) {
  color: var(--suite241-admin-footer-text, #0b508c);
}

@media (min-width: 1200px) {
  .navbar-app .navbar-collapse {
    align-items: center;
    display: flex !important;
    flex-wrap: nowrap;
    gap: 0.75rem;
    min-width: 0;
  }

  .admin-navbar-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0.15rem;
    scrollbar-color: rgba(255, 255, 255, 0.5) transparent;
    scrollbar-width: thin;
  }

  .admin-navbar-scroll::-webkit-scrollbar {
    height: 6px;
  }

  .admin-navbar-scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  .admin-navbar-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.45);
    border-radius: 999px;
  }

  .admin-navbar-scroll:has(.dropdown-menu.show) {
    overflow: visible;
    padding-bottom: 0;
  }

  .navbar-app .navbar-nav {
    display: flex;
    flex-wrap: nowrap;
    min-width: 0;
    width: max-content;
  }

  .navbar-app .dropdown-menu {
    position: absolute;
  }

  .navbar-app .nav-link {
    padding-inline: 0.72rem;
  }
}

@media (max-width: 1199.98px) {
  .admin-navbar-scroll {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0.15rem;
    scrollbar-color: rgba(255, 255, 255, 0.5) transparent;
    scrollbar-width: thin;
  }

  .admin-navbar-scroll::-webkit-scrollbar:vertical {
    display: none;
    width: 0;
  }

  .admin-navbar-scroll::-webkit-scrollbar:horizontal {
    height: 6px;
  }

  .navbar-app .navbar-nav {
    align-items: flex-start;
  }

  .navbar-app .nav-link {
    font-size: 0.72rem;
    max-width: 100%;
    padding: 0.5rem 0.72rem;
    width: fit-content;
  }

  .navbar-app .dropdown-menu .dropdown-item {
    font-size: 0.76rem;
  }

  .admin-navbar-actions {
    align-items: stretch !important;
    flex-direction: column;
    margin-top: 0.75rem;
  }
}

@media (max-width: 575.98px) {
  .navbar-app .nav-link {
    font-size: 0.68rem;
    gap: 0.32rem;
    padding: 0.45rem 0.62rem;
  }

  .navbar-app .dropdown-menu .dropdown-item {
    font-size: 0.72rem;
  }
}
</style>
